// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext, PartyTotal } from '../types';
import { partyStyle } from '../parties';
import { fmtDollars, fmtMoney, fmtNumber, escapeHtml } from '../format';
import { statCard } from '../ui';

export function renderParties(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  const parties = data.summary.parties.filter((p) => p.code !== 'FMR' || p.total > 0);
  const totalAll = parties.reduce((s, p) => s + p.total, 0);
  const totalMembers = parties.reduce((s, p) => s + p.members, 0);

  root.innerHTML = `
    <div class="view-head">
      <h2>Spending by party</h2>
      <p>How the parties compare — both in combined spending and, more fairly, in average spend per member. Click a party to see its members ranked.</p>
    </div>
    <div class="stat-row">
      ${statCard('Parties represented', String(parties.filter((p) => p.code !== 'IND' && p.members).length))}
      ${statCard('Combined expenses', fmtDollars(totalAll))}
      ${statCard('People', fmtNumber(totalMembers))}
      ${statCard('Average per member', fmtMoney(totalAll / Math.max(1, totalMembers)))}
    </div>
    <div class="chart-grid">
      <div class="chart-card">
        <h3>Total expenses by party</h3>
        <div class="subtitle">Combined spend of all members. Bigger parties naturally spend more.</div>
        <div class="bar-list" id="p-total"></div>
      </div>
      <div class="chart-card">
        <h3>Average per member</h3>
        <div class="subtitle">Total divided by member count — a like-for-like comparison independent of party size.</div>
        <div class="bar-list" id="p-avg"></div>
      </div>
    </div>`;

  const navToParty = (code: string) => {
    try {
      const raw = JSON.parse(localStorage.getItem('ampx.rankings') || '{}');
      localStorage.setItem('ampx.rankings', JSON.stringify({ ...raw, party: code, chamber: 'all', metric: 'TOTAL' }));
    } catch { /* ignore */ }
    ctx.goto('rankings');
  };

  const bars = (el: HTMLElement, metric: (p: PartyTotal) => number, sub: (p: PartyTotal) => string) => {
    const sorted = [...parties].sort((a, b) => metric(b) - metric(a));
    const max = Math.max(...sorted.map(metric), 1);
    el.innerHTML = sorted
      .map((p) => {
        const v = metric(p);
        const w = (v / max) * 100;
        const col = partyStyle(p.code).colour;
        const tip = `${escapeHtml(p.name)}\n${escapeHtml(sub(p))}`;
        return `<div class="bar-row" role="button" tabindex="0" data-party="${p.code}" data-tip="${tip}">
          <div class="bar-rank"><span class="dot" style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${col}"></span></div>
          <div class="bar-main">
            <div class="bar-label"><span class="bar-name">${escapeHtml(p.name)}</span><span class="bar-meta">${p.members} member${p.members === 1 ? '' : 's'}</span><span class="bar-value">${fmtDollars(v)}</span></div>
            <div class="bar-track"><div class="bar-fill" style="width:${w.toFixed(1)}%;background:${col}"></div></div>
          </div>
        </div>`;
      })
      .join('');
    el.addEventListener('click', (e) => {
      const row = (e.target as Element).closest<HTMLElement>('.bar-row');
      if (row?.dataset.party) navToParty(row.dataset.party);
    });
    el.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key !== 'Enter' && ke.key !== ' ') return;
      const row = (ke.target as Element).closest<HTMLElement>('.bar-row');
      if (row?.dataset.party) { ke.preventDefault(); navToParty(row.dataset.party); }
    });
  };

  bars(document.getElementById('p-total')!, (p) => p.total, (p) => `${fmtDollars(p.total)} across ${p.members} members`);
  bars(document.getElementById('p-avg')!, (p) => p.avg, (p) => `${fmtDollars(p.avg)} per member (${p.members})`);
}
